export class AudioEngine {
    audioContext;
    analyser;
    microphoneStream = null;
    mediaRecorder = null;
    audioChunks = [];
    audioUrl = null;
    buffer;

    constructor() {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.buffer = new Float32Array(this.analyser.fftSize);
    }

    async startMicrophone() {
        try {
            await this.audioContext.resume();
            this.microphoneStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const source = this.audioContext.createMediaStreamSource(this.microphoneStream);
            source.connect(this.analyser);

            this.mediaRecorder = new MediaRecorder(this.microphoneStream);
            this.audioChunks = [];
            this.mediaRecorder.ondataavailable = (event) => {
                this.audioChunks.push(event.data);
            };
            this.mediaRecorder.start();
        } catch (err) {
            console.error("Error accessing microphone:", err);
            throw err;
        }
    }

    stopMicrophone() {
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
            this.mediaRecorder.onstop = () => {
                const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
                this.audioUrl = URL.createObjectURL(audioBlob);
            }
        }

        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => track.stop());
            this.microphoneStream = null;
        }
    }

    playRecording() {
        if (this.audioUrl) {
            const audio = new Audio(this.audioUrl);
            audio.play();
            return audio;
        }
        return null;
    }

    getPitch() {
        this.analyser.getFloatTimeDomainData(this.buffer);

        // Find basic period using simple autocorrelation
        const detectedPitch = this.autoCorrelate(this.buffer, this.audioContext.sampleRate);
        return detectedPitch;
    }

    // Basic Autocorrelation Algorithm
    autoCorrelate(buf, sampleRate) {
        // Implements the YIN algorithm or a simplified autocorrelation version
        // For this demonstration, we use a simple autocorrelation provided by many examples
        // Ideally, use a library like 'pitchfinder' for robustness, but here's a custom implementation:

        const size = buf.length;
        const rms = Math.sqrt(buf.reduce((acc, val) => acc + val * val, 0) / size);

        if (rms < 0.01) { // Not enough signal
            return -1;
        }

        let r1 = 0, r2 = size - 1, thres = 0.2;
        for (let i = 0; i < size / 2; i++) {
            if (Math.abs(buf[i]) < thres) { r1 = i; break; }
        }
        for (let i = 1; i < size / 2; i++) {
            if (Math.abs(buf[size - i]) < thres) { r2 = size - i; break; }
        }

        const buf2 = buf.slice(r1, r2);
        const c = new Array(buf2.length).fill(0);

        for (let i = 0; i < buf2.length; i++) {
            for (let j = 0; j < buf2.length - i; j++) {
                c[i] = c[i] + buf2[j] * buf2[j + i];
            }
        }

        let d = 0; while (c[d] > c[d + 1]) d++;
        let maxval = -1, maxpos = -1;

        for (let i = d; i < buf2.length; i++) {
            if (c[i] > maxval) {
                maxval = c[i];
                maxpos = i;
            }
        }

        let T0 = maxpos;

        // Parabolic interpolation for better precision
        if (maxpos > 0 && maxpos < c.length - 1) {
            const x1 = c[maxpos - 1], x2 = c[maxpos], x3 = c[maxpos + 1];
            const a = (x1 + x3 - 2 * x2) / 2;
            const b = (x3 - x1) / 2;
            if (a) T0 = T0 - b / (2 * a);
        }

        return sampleRate / T0;
    }

    // Convert Frequency to MIDI note number
    static freqToMidi(frequency) {
        if (frequency === -1) return -1;
        return 12 * (Math.log(frequency / 440) / Math.log(2)) + 69;
    }
}
